/**
 * Where the other sites live.
 *
 * Read from the document rather than compiled in, because it differs per
 * environment — another port locally, another hostname in production — and the
 * meta tag is rewritten at image build time. The bundle stays identical
 * wherever it runs, which is what makes "build once, deploy anywhere" true
 * rather than aspirational.
 */

function fromMeta(name: string, fallback: string): string {
  const meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
  // An empty tag falls back too, not only a missing one — a build that wrote the meta and
  // left it blank has told us nothing, and '' is not an origin. So this is deliberately
  // not `??`, which would accept the blank; the emptiness check says so out loud.
  const value = meta?.content?.trim()
  return value === undefined || value === '' ? fallback : value
}

/** The root site: it owns the account, the credits and the legal pages. */
// standards: const-duplicated-literal exempt -- the browser-side fallback for the root origin, mirroring DEFAULT_ROOT_ORIGIN on the
// server. Each runtime needs its own default because neither can read the other's, and
// a meta tag overrides it at build time anyway.
export const ROOT_ORIGIN = fromMeta('sourcetext-root-origin', 'https://sourcetext.ai')

/** Where a reader goes to manage the account this site only reads. */
export const ACCOUNT_URL = `${ROOT_ORIGIN}/account`

/**
 * Where a reader goes to *get* an account, and to recover one.
 *
 * The root site, like every other account-wide surface: an account covers every
 * corpus, so creating one belongs somewhere that is not one corpus's site. This
 * site only ever links there, and only when the server says sign-up is open.
 */
export const SIGN_UP_URL = `${ROOT_ORIGIN}/signup`
export const PASSWORD_RESET_URL = `${ROOT_ORIGIN}/reset`

/**
 * Where a reader whose address is still unconfirmed asks for a new link.
 *
 * A link out rather than a button here, because the resend route may require
 * a Turnstile token and only the root site renders the widget.
 */
export const NEW_CONFIRMATION_URL = `${ROOT_ORIGIN}/verify`
